import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Phone, Mail, Clock, Users, Euro, AlertTriangle, Shield, Camera, FileText } from "lucide-react";
import SEO from "@/components/SEO";

export default function CampTeilnahmebedingungenPage() {
  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO 
        title="Camp-Teilnahmebedingungen | Fußball Sommer-Camp 2026"
        description="Teilnahmebedingungen und Vertragsbedingungen für das Fußball Sommer-Camp 2026 von Personal Fußballtraining Yasin Aktas."
        keywords="Teilnahmebedingungen, AGB, Camp, Fußball, Sommer-Camp, Vertrag"
      />
      
      {/* Header */}
      <header className="bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/camps" 
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Zurück zu den Camps</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Title */}
        <div className="text-center mb-12">
          <p className="text-[#c1ff72] text-sm uppercase tracking-wider mb-4">Fußball Sommer-Camp 2026</p>
          <h1 className="font-['Bebas_Neue'] text-4xl md:text-5xl lg:text-6xl text-white mb-4">
            CAMP-TEILNAHMEBEDINGUNGEN
          </h1>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Bitte lesen Sie die folgenden Bedingungen sorgfältig durch, bevor Sie Ihr Kind für unser Fußball Sommer-Camp anmelden.
          </p>
        </div>

        {/* Contract Info Box */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#c1ff72]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-[#c1ff72]" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-lg mb-2">Veranstalter</h2>
              <p className="text-[#A1A1AA]">
                <strong className="text-white">Yasin Aktas</strong><br />
                Unterm Hestenberg 2, 58540 Meinerzhagen<br />
                <span className="inline-flex items-center gap-1 mt-1">
                  <Phone className="w-3 h-3" /> 0174 194 499 6
                </span>
                <br />
                <span className="inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> kontakt@fussballtraining-aktas.de
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {/* §1 Vertragsgegenstand */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§1</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">VERTRAGSGEGENSTAND</h2>
            </div>
            <p className="text-[#A1A1AA] leading-relaxed">
              Der Veranstalter führt das <strong className="text-white">Fußball Sommer-Camp 2026</strong> durch. 
              Das Camp umfasst Trainingseinheiten in Technik, Taktik, Athletik sowie spielerische Übungen und Wettkämpfe. 
              Zusätzlich sind Verpflegung, ein Camp-Shirt sowie Challenges mit Preisen enthalten.
            </p>
          </section>

          {/* §2 Leistungsumfang */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§2</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">LEISTUNGSUMFANG</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#c1ff72] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Zeitraum</p>
                  <p className="text-[#A1A1AA]">20. – 23. Juli 2026</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#c1ff72] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Uhrzeit</p>
                  <p className="text-[#A1A1AA]">Täglich 10:00 – 14:00 Uhr</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#c1ff72] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Ort</p>
                  <p className="text-[#A1A1AA]">Am Stadion 1, 58540 Meinerzhagen</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-[#c1ff72] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Altersgruppe</p>
                  <p className="text-[#A1A1AA]">Kinder von 6 bis 13 Jahren (Aufteilung in Gruppen)</p>
                </div>
              </div>
            </div>
          </section>

          {/* §3 Teilnahmegebühr */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§3</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">TEILNAHMEGEBÜHR UND ZAHLUNG</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-[#c1ff72]/10 border border-[#c1ff72]/30 rounded-lg p-4">
                <Euro className="w-6 h-6 text-[#c1ff72]" />
                <div>
                  <p className="text-white font-semibold text-lg">180,00 € pro Kind</p>
                  <p className="text-[#A1A1AA] text-sm">Zahlbar bis spätestens 10.07.2026</p>
                </div>
              </div>
              <div className="text-[#A1A1AA] space-y-2">
                <p><strong className="text-white">Zahlungsmethoden:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Überweisung</li>
                  <li>Barzahlung</li>
                  <li>PayPal</li>
                </ul>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Hinweis:</strong> Die Teilnahme ist nur bei rechtzeitigem Zahlungseingang gesichert.</span>
                </p>
              </div>
            </div>
          </section>

          {/* §4 Pflichten */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§4</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">PFLICHTEN DES TEILNEHMERS / DER ERZIEHUNGSBERECHTIGTEN</h2>
            </div>
            <ul className="space-y-3 text-[#A1A1AA]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#c1ff72] rounded-full mt-2 flex-shrink-0"></span>
                <span>Pünktliches Erscheinen in geeigneter Sportkleidung</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#c1ff72] rounded-full mt-2 flex-shrink-0"></span>
                <span>Mitteilung von gesundheitlichen Einschränkungen/Allergien vor Trainingsbeginn</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#c1ff72] rounded-full mt-2 flex-shrink-0"></span>
                <span>Bei Minderjährigen: Unterschrift der Erziehungsberechtigten erforderlich</span>
              </li>
            </ul>
          </section>

          {/* §5 Rücktritt und Stornierung */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§5</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">RÜCKTRITT UND STORNIERUNG</h2>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                  <p className="text-green-400 font-semibold mb-1">30+ Tage vorher</p>
                  <p className="text-white text-lg font-bold">100%</p>
                  <p className="text-[#A1A1AA] text-sm">Rückerstattung</p>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                  <p className="text-yellow-400 font-semibold mb-1">14-30 Tage vorher</p>
                  <p className="text-white text-lg font-bold">50%</p>
                  <p className="text-[#A1A1AA] text-sm">Rückerstattung</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                  <p className="text-red-400 font-semibold mb-1">&lt;14 Tage / Nichterscheinen</p>
                  <p className="text-white text-lg font-bold">0%</p>
                  <p className="text-[#A1A1AA] text-sm">Keine Rückerstattung</p>
                </div>
              </div>
              <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4">
                <p className="text-[#A1A1AA] text-sm">
                  <strong className="text-white">Bei Krankheit mit ärztlichem Attest:</strong> 50% Rückerstattung oder Gutschrift für das nächste Camp oder Einzeltraining nach Absprache.
                </p>
              </div>
            </div>
          </section>

          {/* §6 Haftung */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§6</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">HAFTUNG & GESUNDHEIT</h2>
            </div>
            <ul className="space-y-3 text-[#A1A1AA]">
              <li className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>Die Teilnahme erfolgt auf eigenes Risiko.</span>
              </li>
              <li className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>Der Veranstalter haftet nur bei grober Fahrlässigkeit oder Vorsatz.</span>
              </li>
              <li className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>Für mitgebrachte Gegenstände wird keine Haftung übernommen.</span>
              </li>
            </ul>
          </section>

          {/* §7 Versicherung */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§7</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">VERSICHERUNG</h2>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#c1ff72] mt-0.5 flex-shrink-0" />
              <p className="text-[#A1A1AA]">
                Die Teilnehmer sind über ihre Erziehungsberechtigten für ausreichenden <strong className="text-white">Unfall- und Haftpflichtschutz</strong> verantwortlich. Der Veranstalter übernimmt hierfür keine Haftung.
              </p>
            </div>
          </section>

          {/* §8 Datenschutz */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§8</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">DATENSCHUTZ</h2>
            </div>
            <div className="space-y-4">
              <p className="text-[#A1A1AA]">
                Die personenbezogenen Daten werden ausschließlich für die Organisation des Camps verwendet.
              </p>
              <div className="flex items-start gap-3 bg-[#1A1A1A] border border-white/10 rounded-lg p-4">
                <Camera className="w-5 h-5 text-[#c1ff72] mt-0.5 flex-shrink-0" />
                <p className="text-[#A1A1AA] text-sm">
                  <strong className="text-white">Foto- und Videoaufnahmen:</strong> Die Einwilligung zur Nutzung von Bildern/Videos für Marketingzwecke wird separat im Anmeldeformular abgefragt.
                </p>
              </div>
              <p className="text-[#A1A1AA] text-sm">
                Weitere Informationen finden Sie in unserer{" "}
                <Link to="/datenschutz" className="text-[#c1ff72] hover:underline">
                  Datenschutzerklärung
                </Link>.
              </p>
            </div>
          </section>

          {/* §9 Vertragsdauer */}
          <section className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#c1ff72]/20 rounded-lg flex items-center justify-center text-[#c1ff72] font-bold text-sm">§9</span>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white">VERTRAGSDAUER</h2>
            </div>
            <p className="text-[#A1A1AA]">
              Dieser Vertrag gilt für den Zeitraum des <strong className="text-white">Fußball Sommer-Camps 2026 (20. – 23. Juli 2026)</strong> und endet automatisch mit Abschluss der Veranstaltung.
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-[#A1A1AA] mb-6">
            Mit der Anmeldung akzeptieren Sie diese Teilnahmebedingungen.
          </p>
          <Link
            to="/camps"
            className="inline-flex items-center justify-center gap-2 bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold px-8 py-4 transition-colors"
          >
            Jetzt zum Camp anmelden
          </Link>
        </div>

        {/* Footer Info */}
        <div className="mt-16 pt-8 border-t border-white/10 text-center text-[#71717A] text-sm">
          <p>Stand: Juli 2026</p>
          <p className="mt-2">
            Fußballtraining Aktas · Yasin Aktas · Unterm Hestenberg 2 · 58540 Meinerzhagen
          </p>
        </div>
      </div>
    </main>
  );
}
